import { useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { parseTelegramText } from "../../utils/parseTelegramText";
import { Button } from "@/shared/components/ui/button";
import { toast } from "sonner";
import type { TourCard } from "../../types/history";
import { formatFilterLine } from "../../utils/formatFilter";
import { mergeTelegramPosts } from "../../utils/mergeTelegramPosts";

interface TourModalProps {
  card: TourCard | null;
  open: boolean;
  onClose: () => void;
}

export function TourModal({ card, open, onClose }: TourModalProps) {
  // Бэкенд публикует прогоны автоматически — post уже в /history. Склеиваем посты
  // завершённых прогонов в один текст для Telegram (без запросов на публикацию).
  const mergedText = useMemo(() => {
    if (!card || card.status === "pending") return "";
    const posts = card.items
      .filter((i) => i.status === "completed")
      .map((i) => i.post);
    return mergeTelegramPosts(posts);
  }, [card]);

  if (!card) return null;

  // Pending: постов ещё нет — показываем список фильтров, которые сейчас в обработке.
  if (card.status === "pending") {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Фильтры в обработке</DialogTitle>
          </DialogHeader>

          <div className="overflow-y-auto flex-1 space-y-2">
            {card.items.map((item, i) => (
              <p key={i} className="text-sm leading-snug text-foreground">
                {formatFilterLine(item.filter)}
              </p>
            ))}
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={onClose}>
              Закрыть
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const copyText = () => {
    navigator.clipboard.writeText(mergedText);
    toast.success("Текст скопирован");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Горящие предложения</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1">
          {mergedText.trim() ? (
            <p className="text-sm leading-relaxed whitespace-pre-wrap text-muted-foreground">
              {parseTelegramText(mergedText)}
            </p>
          ) : (
            <p className="text-sm text-muted-foreground">
              Предложения не найдены
            </p>
          )}
        </div>

        <div className="flex justify-end gap-2">
          {mergedText.trim() && (
            <Button onClick={copyText}>Скопировать для Telegram</Button>
          )}
          <Button variant="outline" onClick={onClose}>
            Закрыть
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
